/**
 * A range picked by its bounds rather than its length, carried in the same `time_range` value as
 * the presets: an ISO 8601 interval of two UTC instants, `2026-10-12T11:39Z/2026-10-13T11:39Z`.
 * The backend (`app/dashboards/time_range.py`) reads it as is, and derives the chart step from
 * its length, as it does for a preset.
 */

import {
  DAY_MONTH_ABBREVIATED_HOUR_MINUTE,
  DAY_MONTH_ABBREVIATED_YEAR_HOUR_MINUTE,
} from '@scality/core-ui/dist/components/date/FormattedDateTime';

const ABSOLUTE_RANGE_SEPARATOR = '/';

export type AbsoluteTimeRange = { start: Date; end: Date };

/** `2026-10-12T11:39Z`: the inputs pick minutes, so seconds would only lengthen the URL. */
const toUtcMinute = (date: Date): string => `${date.toISOString().slice(0, 16)}Z`;

export const toAbsoluteTimeRange = ({ start, end }: AbsoluteTimeRange): string =>
  `${toUtcMinute(start)}${ABSOLUTE_RANGE_SEPARATOR}${toUtcMinute(end)}`;

/** Date, time and offset all required: `Date` alone would read `1` as the year 2001. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** The bounds of an absolute range, or `undefined` for a preset or anything that bounds nothing. */
export const parseAbsoluteTimeRange = (value: string): AbsoluteTimeRange | undefined => {
  const [startText, endText, ...rest] = value.trim().split(ABSOLUTE_RANGE_SEPARATOR);
  if (endText === undefined || rest.length > 0) return undefined;
  if (!ISO_INSTANT.test(startText) || !ISO_INSTANT.test(endText)) return undefined;
  const start = new Date(startText);
  const end = new Date(endText);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) return undefined;
  return { start, end };
};

export const isAbsoluteTimeRange = (value: string): boolean => parseAbsoluteTimeRange(value) !== undefined;

/** core-ui's `day-month-abbreviated-hour-minute` cleanup, which its formatters leave to the caller. */
const formatBound = (format: Intl.DateTimeFormat, date: Date): string =>
  format.format(date).replace(',', '').replace(/Sept/g, 'Sep');

/** "06 Oct 08:00 → 07 Oct 18:30" in the reader's zone; the year only when it is not this one. */
export const absoluteTimeRangeLabel = ({ start, end }: AbsoluteTimeRange, now = new Date()): string => {
  const year = now.getFullYear();
  const format =
    start.getFullYear() === year && end.getFullYear() === year
      ? DAY_MONTH_ABBREVIATED_HOUR_MINUTE
      : DAY_MONTH_ABBREVIATED_YEAR_HOUR_MINUTE;
  return `${formatBound(format, start)} → ${formatBound(format, end)}`;
};

const pad = (value: number) => String(value).padStart(2, '0');

/** A `datetime-local` input's value: the reader's wall clock, to the minute. */
export const toDateTimeInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** Read back as local time, which is what the spec says a date-time string without offset means. */
export const fromDateTimeInputValue = (value: string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};
